import { Modal as AntModal, ModalFuncProps, ModalProps } from 'antd'
import * as React from 'react'

import { CheckCircleIcon, CloseCircleIcon, InfoCircleIcon, LogOutIcon, WarningIcon } from '../../icons'
import theme from '../../theme'
import { ModalType } from '../../types'
import { RootSpan } from './styles'

export interface ModalRefType {
  triggerOpen: () => void
  close: () => void
}

export interface IModalProps extends ModalFuncProps {
  promptType?: ModalType
}

const Modal = React.forwardRef<ModalRefType, IModalProps>((props, ref) => {
  const [innerModal, contextHolder] = AntModal.useModal()
  const [currentModal, setCurrentModal] = React.useState<
    | {
        destroy: () => void
        update: (configUpdate: ModalProps) => void
      }
    | undefined
  >()
  const rootRef = React.useRef<HTMLElement | null>(null)

  // Enable opening/closing modal with ref
  React.useImperativeHandle(ref, () => ({
    triggerOpen: openModal,
    close: closeModal,
  }))

  // make sure to clear modals on unmount
  React.useEffect(() => {
    return () => {
      AntModal.destroyAll()
    }
  }, [])

  const closeModal = React.useCallback(() => {
    currentModal?.destroy()
  }, [currentModal])

  const openModal = React.useCallback(() => {
    let icon
    switch (props.promptType) {
      case ModalType.SUCCESS:
        icon = <CheckCircleIcon style={{ color: theme.colors.green5 }} />
        break
      case ModalType.WARNING:
        icon = <WarningIcon style={{ color: theme.colors.yellow7 }} />
        break
      case ModalType.ERROR:
        icon = <CloseCircleIcon style={{ color: theme.colors.red6 }} />
        break
      case ModalType.INFO:
        icon = <InfoCircleIcon style={{ color: theme.colors.blue5 }} />
        break
      case ModalType.LOGOUT:
        icon = <LogOutIcon id="nova-LogOutIcon" style={{ color: theme.colors.yellow7 }} />
        break
      default:
        icon = <CheckCircleIcon style={{ color: theme.colors.green5 }} />
    }

    const modal = innerModal.confirm({
      ...props,
      icon: icon,
      getContainer: rootRef.current ?? undefined,
      onCancel: () => {
        props.onCancel && props.onCancel()
        currentModal?.destroy()
      },
      onOk: () => {
        props.onOk && props.onOk()
        currentModal?.destroy()
      },
    })
    setCurrentModal(modal)
  }, [props, currentModal, setCurrentModal])

  // render context holder to apply antd context to the modal (styles from Design Token)
  return (
    <RootSpan id="nova-shared-Modal-root" ref={rootRef} modalPromptType={props.promptType}>
      {contextHolder}
    </RootSpan>
  )
})

Modal.displayName = 'Modal'
export default Modal
